import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'

import { FormattedPrice } from '@src/blocks/currency/currency.component'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, {
  fireThirdPartyProductEvents,
  getEarnCreditTagFor3PVendor,
  getUnitForPerServing,
  shouldShowPricePerUnit,
} from '@src/quince-site-common/src/helpers/productHelper'
import { getPriceStyleKey } from '@src/quince-site-common/src/utils/festivalOffersUtility'

import { PURCHASE_TYPES, PurchaseType } from '../purchaseTypeSelector/PurchaseTypeSelector'

import styles from './ProductInfoPrice.module.scss'

const STYLE_KEY_MAP = {
  festival: 'festivalOffer' as const,
  subscription: 'subscriptionOffer' as const,
  default: 'default' as const,
}

export interface ProductInfoPriceProps {
  product?: Product
  formattedSalePrice: string
  formattedTraditionalPrice?: string
  savingPercentage?: number
  formattedFestivalOfferPrice?: string
  showDiscount?: boolean
  pricePerServing?: number
  traditionalPricePerServing?: number
  festivalPricePerUnit?: number
  subscriptionOfferPricePerUnit?: number
  hideSavings?: boolean
  showNewPdpDesignV2?: boolean
  isFinalSaleProduct?: boolean
  isB2BFinalSaleProduct?: boolean
  formattedSubscriptionPrice?: string
  purchaseOption?: PurchaseType
  updatedDesignForV2?: boolean
  isVariantThirdParty?: boolean
  selectedOptions?: ProductVariantOption[]
  brandName?: string
  pdpPriceRedesign?: boolean
}

const DIFFERENCE_TO_IGNORE = 0.1

function ProductInfoPrice({
  product,
  formattedSubscriptionPrice,
  formattedSalePrice,
  formattedTraditionalPrice,
  savingPercentage,
  formattedFestivalOfferPrice,
  showDiscount = true,
  pricePerServing,
  traditionalPricePerServing,
  festivalPricePerUnit,
  subscriptionOfferPricePerUnit,
  hideSavings,
  showNewPdpDesignV2: showNewPdpDesignV2Prop,
  isFinalSaleProduct,
  isB2BFinalSaleProduct,
  purchaseOption,
  updatedDesignForV2,
  isVariantThirdParty,
  selectedOptions,
  brandName,
  pdpPriceRedesign = false,
}: ProductInfoPriceProps) {
  const { t } = useTranslation('common')
  const showNewPdpDesignV2 = pdpPriceRedesign || showNewPdpDesignV2Prop
  const isDiscountLabelPresent =
    showDiscount && formattedTraditionalPrice
      ? Number(formattedTraditionalPrice) - Number(formattedSalePrice) > DIFFERENCE_TO_IGNORE
      : false
  const showPricePerUnit = shouldShowPricePerUnit(product)
  const hasPerUnitPricing = showPricePerUnit && !!pricePerServing && !!traditionalPricePerServing
  const perUnitSavingsPercentage = hasPerUnitPricing
    ? productHelper.getSavingPercentage(traditionalPricePerServing, pricePerServing)
    : ''

  const showBestMarketValue = isVariantThirdParty && !!brandName

  const showSavingPercentage =
    !formattedFestivalOfferPrice &&
    !(formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE) &&
    formattedTraditionalPrice &&
    savingPercentage &&
    isDiscountLabelPresent &&
    !hasPerUnitPricing &&
    !hideSavings &&
    !showBestMarketValue

  const priceWrapperStylesToApply = {
    festivalOffer: [styles.priceWrapper__festivalOffer],
    subscriptionOffer: [styles.priceWrapper__subscriptionOffer],
    default: [],
  }
  const priceWrapperStylesToApplyPdpDesignV2 = {
    festivalOffer: [styles.priceWrapper__festivalOffer_newDesignV2],
    subscriptionOffer: [styles.priceWrapper__subscriptionOffer_newDesignV2],
    default: [],
  }

  const priceWrapperMainValueStylesToApply = {
    festivalOffer: [styles.festival_offer],
    subscriptionOffer: [styles.subscription_offer],
    default: [],
  }

  const priceWrapperMainValueStylesToApplyPdpDesignV2 = {
    festivalOffer: [styles.festival_offer_newDesignV2],
    subscriptionOffer: [styles.subscription_offer_newDesignV2],
    default: [],
  }

  const showPerUnitSavingsPercentage =
    perUnitSavingsPercentage &&
    !formattedFestivalOfferPrice &&
    !(formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE) &&
    !hideSavings &&
    !showBestMarketValue
  const perUnitLabel = getUnitForPerServing(product)
  const unitForPerServing = perUnitLabel ? t(perUnitLabel) : ''
  const offerStyleKey =
    STYLE_KEY_MAP[
      getPriceStyleKey(
        !!formattedFestivalOfferPrice,
        !!formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE,
      )
    ]
  const applicableProductTags = productHelper.getApplicableTags({
    product,
    selectedOptions: selectedOptions ?? [],
    productTags: product?.tags,
  })
  const earnCreditTagFor3PVendor = isVariantThirdParty ? getEarnCreditTagFor3PVendor(applicableProductTags) : undefined

  useEffect(() => {
    if (isVariantThirdParty && product && selectedOptions) {
      fireThirdPartyProductEvents(product, selectedOptions, !!earnCreditTagFor3PVendor, showBestMarketValue)
    }
  }, [])
  return (
    <div>
      <div
        className={classnames(
          styles.priceWrapper,
          showNewPdpDesignV2 && styles.priceWrapper_newDesignV2,
          ...priceWrapperStylesToApply[offerStyleKey],
          ...priceWrapperStylesToApplyPdpDesignV2[showNewPdpDesignV2 ? offerStyleKey : 'default'],
        )}
      >
        <div className={classnames(styles.priceWrapper__main, styles.priceWrapper__main__space)}>
          <div
            className={classnames(
              styles.priceWrapper__main__value,
              styles.priceWrapper__main__value__qp,
              showNewPdpDesignV2 && styles.priceWrapper__main__value__qp_newDesignV2,
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              (formattedFestivalOfferPrice ||
                (formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE)) &&
                showNewPdpDesignV2 &&
                styles.priceWrapper__main__value__qp_newDesignV2_festivalPrice,
              isB2BFinalSaleProduct && !formattedTraditionalPrice && styles.b2bPriceWrapper,
              updatedDesignForV2 && styles.priceWrapper__main__value__qp_updatedDesignV2,
            )}
          >
            <span className={classnames('sales_price_selector_for_force_update')}>
              <FormattedPrice amount={formattedSalePrice} />
            </span>
            {hasPerUnitPricing &&
              !formattedFestivalOfferPrice &&
              !(formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE) &&
              unitForPerServing && (
                <span
                  className={classnames(
                    styles.priceWrapper__main__value__perserving,
                    showNewPdpDesignV2 && styles.priceWrapper__main__value__perserving_newDesignV2,
                  )}
                >
                  {'('}
                  <span
                    className={classnames(styles.priceWrapper__main__value__perserving__traditional, {
                      [styles.priceWrapper__main__value__perserving__traditional_newPdpDesignV2 ?? '']:
                        showNewPdpDesignV2,
                    })}
                  >
                    <FormattedPrice amount={traditionalPricePerServing} />
                  </span>{' '}
                  <FormattedPrice amount={pricePerServing} />/{unitForPerServing}
                  {')'}
                </span>
              )}
            {earnCreditTagFor3PVendor && !formattedFestivalOfferPrice && (
              <div
                className={classnames(
                  styles.priceWrapper__main__earnCreditTag,
                  pdpPriceRedesign && styles.priceWrapper__main__earnCreditTag_regularWeight,
                )}
              >
                {earnCreditTagFor3PVendor}
              </div>
            )}
          </div>
          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
          {((formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE) ||
            formattedFestivalOfferPrice) && (
            <div
              className={classnames(
                styles.priceWrapper__main__value,
                styles.priceWrapper__main__value__qp,
                ...priceWrapperMainValueStylesToApply[offerStyleKey],
                ...priceWrapperMainValueStylesToApplyPdpDesignV2[showNewPdpDesignV2 ? offerStyleKey : 'default'],
              )}
            >
              <span className="festival_sale_price_selector_for_force_update">
                <FormattedPrice
                  amount={
                    formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE
                      ? formattedSubscriptionPrice
                      : formattedFestivalOfferPrice
                        ? formattedFestivalOfferPrice
                        : ''
                  }
                />
              </span>
            </div>
          )}
          {earnCreditTagFor3PVendor && formattedFestivalOfferPrice && (
            <div
              className={classnames(
                styles.priceWrapper__main__earnCreditTag,
                styles.priceWrapper__main__earnCreditTag_leftMargin,
                pdpPriceRedesign && styles.priceWrapper__main__earnCreditTag_regularWeight,
              )}
            >
              {earnCreditTagFor3PVendor}
            </div>
          )}
          {isFinalSaleProduct && !isB2BFinalSaleProduct && (
            <div
              className={classnames(
                styles.priceWrapper__main__finalSale,
                showNewPdpDesignV2 && styles.priceWrapper__main__finalSale_newDesignV2,
                isVariantThirdParty && styles.priceWrapper__main__thirdPartyFinalSale,
              )}
            >
              <span>{t('final_sale')} </span>
            </div>
          )}
          {showSavingPercentage && !showNewPdpDesignV2 && (
            <div
              className={classnames(
                styles.priceWrapper__main__text,
                styles.priceWrapper__main__margin,
                updatedDesignForV2 && styles.priceWrapper__main__text_updatedDesignV2,
              )}
            >
              {t('you_save', { percentage: savingPercentage })}
            </div>
          )}

          {showPerUnitSavingsPercentage && !showNewPdpDesignV2 && (
            <div
              className={classnames(
                styles.priceWrapper__main__text,
                updatedDesignForV2 && styles.priceWrapper__main__text_updatedDesignV2,
              )}
            >
              {t('save')} <span>{perUnitSavingsPercentage}</span>
              {t('compared_to_traditional_retail')}
            </div>
          )}

          {showBestMarketValue && !formattedFestivalOfferPrice && (
            <div
              className={classnames(
                styles.priceWrapper__main__thirdPartyMsg,
                pdpPriceRedesign && styles.priceWrapper__main__thirdPartyMsg_pdpPriceRedesign,
              )}
            >
              {t('best_market_value')}
            </div>
          )}
        </div>

        {formattedTraditionalPrice && isDiscountLabelPresent && !hasPerUnitPricing && !showBestMarketValue && (
          <div className={classnames(styles.priceWrapper__main)}>
            <div
              className={classnames(
                styles.priceWrapper__main__value,
                styles.priceWrapper__main__value__rp,
                formattedFestivalOfferPrice && styles.priceWrapper__main__margin,
                showNewPdpDesignV2 && styles.priceWrapper__main__value__rp_newDesignV2,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                (formattedFestivalOfferPrice ||
                  (formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE)) &&
                  showNewPdpDesignV2 &&
                  styles.priceWrapper__main__value__rp_newDesignV2_festivalPrice,
                pdpPriceRedesign &&
                  (formattedFestivalOfferPrice ||
                    (formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE)) &&
                  showNewPdpDesignV2 &&
                  styles.priceWrapper__main__value__rp_pdpPriceRedesign,
                updatedDesignForV2 && styles.priceWrapper__main__value__rp_updatedDesignV2,
              )}
            >
              <span className="traditionalPrice_selector_for_force_update">
                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                {(formattedFestivalOfferPrice ||
                  (formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE)) &&
                showNewPdpDesignV2 ? (
                  t('vs_traditional_retail', { value: formattedTraditionalPrice })
                ) : (
                  <FormattedPrice amount={formattedTraditionalPrice} />
                )}
              </span>
            </div>
            {!showNewPdpDesignV2 && (
              <div
                className={classnames(
                  styles.priceWrapper__main__text,
                  styles.priceWrapper__main__text__rp,
                  styles.priceWrapper__main__margin,
                  updatedDesignForV2 && styles.priceWrapper__main__text_updatedDesignV2,
                )}
              >
                {t('traditional_retail')}
              </div>
            )}
          </div>
        )}
        {showBestMarketValue && formattedFestivalOfferPrice && (
          <div
            className={classnames(
              styles.priceWrapper__main__thirdPartyMsg,
              pdpPriceRedesign && styles.priceWrapper__main__thirdPartyMsg_pdpPriceRedesign,
            )}
          >
            {t('best_market_value')}
          </div>
        )}

        {isB2BFinalSaleProduct && !isFinalSaleProduct && (
          <div
            className={classnames(
              styles.priceWrapper__main__finalSale,
              showNewPdpDesignV2 && styles.priceWrapper__main__finalSale_newDesignV2,
              styles.b2bFinalSaleTextWrapper,
            )}
          >
            <span>{t('final_sale')} </span>
          </div>
        )}
      </div>
      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
      {showNewPdpDesignV2 && (showSavingPercentage || showPerUnitSavingsPercentage) && (
        <div
          className={classnames(
            styles.priceWrapper__main__text,
            styles.priceWrapper__main__text_newDesignV2,
            styles.priceWrapper__main__margin,
          )}
        >
          {t('save')} <span>{showSavingPercentage ? savingPercentage : perUnitSavingsPercentage}</span>
          {t('compared_to_traditional_retail')}
        </div>
      )}

      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
      {(festivalPricePerUnit || (subscriptionOfferPricePerUnit && purchaseOption === PURCHASE_TYPES.SUBSCRIBE)) &&
        traditionalPricePerServing &&
        unitForPerServing && (
          <div className={classnames(styles.priceWrapper__main__text, styles.priceWrapper__main__text_newDesignV2)}>
            <span className={styles.priceWrapper__main__value__perserving__traditional}>
              <FormattedPrice amount={traditionalPricePerServing} />
            </span>{' '}
            <FormattedPrice
              amount={
                subscriptionOfferPricePerUnit && purchaseOption === PURCHASE_TYPES.SUBSCRIBE
                  ? subscriptionOfferPricePerUnit
                  : festivalPricePerUnit
                    ? festivalPricePerUnit
                    : ''
              }
            />
            /{t('per_unit_vs_traditional_retail', { value: unitForPerServing })}
          </div>
        )}
    </div>
  )
}

export default ProductInfoPrice
